/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, {
    useEffect, useRef, useState, useCallback,
} from 'react';
import {
    object, func, bool,
} from 'prop-types';
import {
    makeStyles, IconButton,
} from '@material-ui/core';
import { brandSurveyScript } from '../MultiBrandChatController/utils/qualtricsScripts';
import ConversationalQualtricsFeedBack from './ConversationalQualtricsFeedback';

const useStyles = makeStyles((theme) => ({
    feedBackButton: {
        zIndex: '2200000',
        borderTop: `solid ${theme.palette.cms?.primary} 1px !important`,
        borderBottom: `solid ${theme.palette.cms?.primary} 1px !important`,
        borderRadius: '0 !important',
        height: '70px !important',
    },
    feedBackButtonTop: {
        borderTop: 'none !important',
        backgroundColor: 'unset !important',
        height: '70px !important',
    },
    feedBackColor: {
        color: 'white !important',
    },
    textStyles: {
        fontSize: '12px !important',
        paddingTop: '8px !important',
    },
    reversedColor: {
        backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary} !important`,
        color: '#FFFFFF !important',
    },
    openButton: {
        zIndex: '2200000',
        position: 'fixed',
        bottom: 160,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        border: (props) => (props.isChatIconDisplayed || props.isFeedbackButtonDisplayed || props.showTopButton ? `solid ${theme.palette.cms?.primary} 1px` : 'none'),
        backgroundColor: 'white',
        alignItems: 'center',
        width: '70px',
        borderRight: 'none',
        '& svg': {
            color: theme.palette.cms?.primary,
            fontSize: '30px',
            width: 'auto',
            display: 'block',
            margin: '0 auto',
        },
        '& span': {
            display: 'block',
            color: theme.palette.cms?.primary,
            fontSize: '1rem',
            textAlign: 'center',
        },
        '& button': {
            padding: '10px 5px',
            color: theme.palette.cms?.primary,
            cursor: 'pointer',
            fontSize: '1.2rem',
            backgroundColor: 'white',
            borderBottom: (props) => (!props.isChatIconDisplayed || !props.isFeedbackButtonDisplayed ? `solid ${theme.palette.cms?.primary} 1px` : ''),
            height: '70px',
            width: '100%',
            borderRadius: '0',
        },
        '& path': {
            fill: theme.palette.cms?.primary,
        },
        [theme.breakpoints.down('600')]: {
            right: 20,
        },
    },
    hidden: {
        visibility: 'hidden',
    },
}));

const ConversationalQualtricsFeedBackContainer = ({
    brand, isMobile, featureFlags, feedbackMinimized, setFeedbackMinimized, feedbackOpen, setFeedbackOpen, isFeedbackButtonDisplayed,
}) => {
    const isFeedbackOpened = useRef(false);
    const feedbackRef = useRef(null);
    const isFloraChatQualtricsSurveyButtonEnabled  = featureFlags['is-flora-chat-persistent-qualtrics-survey-button-enabled'];
    const [surveyFetched, setSurveyFetched] = useState(false);
    const classes = useStyles({
        isFeedbackButtonDisplayed,
    });
    const setIsFeedbackOpened = (toggle) => {
        if (toggle !== undefined && isFloraChatQualtricsSurveyButtonEnabled) {
            isFeedbackOpened.current = toggle;
        }
    };
    const openFeedback = useCallback(() => {
        if (isFloraChatQualtricsSurveyButtonEnabled) {
            if (!feedbackOpen) {
                setFeedbackOpen(true);
                isFeedbackOpened.current = true;
            }
            if (!surveyFetched) {
                brandSurveyScript(brand.code);
                setSurveyFetched(true);
            }
            if (feedbackMinimized) {
                setFeedbackMinimized(false);
            }
        }
    }, [
        isFloraChatQualtricsSurveyButtonEnabled,
        feedbackOpen,
        surveyFetched,
        feedbackMinimized,
        brand.code,
        setFeedbackOpen,
        setFeedbackMinimized,
        setSurveyFetched,
    ]);
    const handleClickOutside = (event) => {
        if ((isFeedbackOpened?.current) && isFloraChatQualtricsSurveyButtonEnabled && (feedbackRef?.current && !feedbackRef?.current.contains(event?.target))) {
            setFeedbackMinimized(!feedbackMinimized);
        }
    };
    useEffect(() => {
        if (isFloraChatQualtricsSurveyButtonEnabled) {
            document.addEventListener('click', handleClickOutside, true);
            return () => {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }
        return () => {};
    }, [feedbackRef]);

    if (isMobile || !isFloraChatQualtricsSurveyButtonEnabled) {
        return null;
    }

    return (
        <>

            <ConversationalQualtricsFeedBack
                open={feedbackOpen}
                setFeedbackOpen={setFeedbackOpen}
                feedbackMinimized={feedbackMinimized}
                setFeedbackMinimized={setFeedbackMinimized}
                setSurveyFetched={setSurveyFetched}
                feedbackRef={feedbackRef}
                setIsFeedbackOpened={setIsFeedbackOpened}
            />
            {isFeedbackButtonDisplayed
            && (
                <div className={`${classes.openButton}${!isFeedbackButtonDisplayed ? classes.hidden : ''}`}>
                    <IconButton
                        aria-hidden="true"
                        tabIndex="-1"
                        className={`${feedbackMinimized ? classes.reversedColor : classes.feedBackButton} ${classes.feedBackButtonTop} ${!isFeedbackButtonDisplayed ? classes.hidden : ''}`}
                        onClick={openFeedback}
                    >
                        <span className={feedbackMinimized ? classes.feedBackColor : ''}>[+]</span>
                        <span className={`${classes.textStyles} ${feedbackMinimized ? classes.feedBackColor : ''}`}>Feedback</span>
                    </IconButton>
                </div>
            )}
        </>
    );
};

ConversationalQualtricsFeedBackContainer.propTypes = {
    brand: object.isRequired,
    featureFlags: object.isRequired,
    isMobile: bool.isRequired,
    feedbackMinimized: bool.isRequired,
    setFeedbackMinimized: func.isRequired,
    feedbackOpen: bool.isRequired,
    setFeedbackOpen: func.isRequired,
    isFeedbackButtonDisplayed: bool.isRequired,
};

export default ConversationalQualtricsFeedBackContainer;
